import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Users, Target, Calendar, Mail, Check, X, Loader2, 
  Shield, ChevronDown, ChevronUp, Eye, Crown, Star, Zap,
  TrendingUp, Clock, Phone, CheckCircle, AlertCircle, FileText,
  Video, Plus, Trash2, Send
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const TIER_CONFIG = {
  baseline: { name: 'Baseline', icon: Zap, color: '#60A5FA', badge: 'BAS' },
  academy_major: { name: 'Academy Major', icon: Star, color: '#F97316', badge: 'MAJ' },
  elite_pivot: { name: 'Elite Pivot', icon: Crown, color: '#D4AF37', badge: 'ELITE' }
};

const ZONE_CONFIG = {
  clogging: { label: 'Clogging', color: '#EF4444' },
  coasting: { label: 'Coasting', color: '#F59E0B' },
  elite: { label: 'Elite', color: '#D4AF37' }
};

const AcademyAdminPortal = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('leads');
  
  // Data states
  const [leads, setLeads] = useState([]);
  const [roster, setRoster] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [coachVideos, setCoachVideos] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [submissionsBreakdown, setSubmissionsBreakdown] = useState({});
  const [dataLoading, setDataLoading] = useState(false);
  
  // Coach's Corner states
  const [addVideoOpen, setAddVideoOpen] = useState(false);
  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    video_url: '',
    duration: '',
    is_featured: false
  });
  const [addingVideo, setAddingVideo] = useState(false);
  
  // Hype Sequence states
  const [sendingHype, setSendingHype] = useState(false);
  const [hypeEmailType, setHypeEmailType] = useState(null);
  
  // Expanded rows
  const [expandedRows, setExpandedRows] = useState({});
  
  // Scouting report dialog state
  const [scoutingReportOpen, setScoutingReportOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [scoutingReport, setScoutingReport] = useState({
    week: 1,
    // SmartScore Metrics
    skillMastery: '',
    currentSmartScore: '',
    growthDelta: '',
    smartScoreTake: '',
    // Executive Functioning
    focusArea: '',
    focusStatus: 'ACCELERATING',
    executiveTake: '',
    // The Next Play
    primaryObjective: '',
    secondaryObjective: '',
    coachNote: ''
  });
  const [sendingReport, setSendingReport] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await api.post('/academy/admin/verify', { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        localStorage.setItem('academy_admin_auth', 'true');
        toast.success('Welcome to Command Center');
        loadAllData();
      }
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const isAuth = localStorage.getItem('academy_admin_auth');
    if (isAuth) {
      setIsAuthenticated(true);
      loadAllData();
    }
  }, []);

  const loadAllData = async () => {
    setDataLoading(true);
    try {
      const [leadsRes, rosterRes, consultationsRes, videosRes, submissionsRes] = await Promise.all([
        api.get('/academy/admin/leads'),
        api.get('/academy/admin/roster'),
        api.get('/academy/admin/consultations'),
        api.get('/academy/coach-corner/videos'),
        api.get('/academy/admin/submissions')
      ]);
      
      setLeads(leadsRes.data.leads || []);
      setRoster(rosterRes.data.roster || []);
      setConsultations(consultationsRes.data.consultations || []);
      setSubmissions(submissionsRes.data.submissions || []);
      setSubmissionsBreakdown(submissionsRes.data.breakdown || {});
      
      // Combine featured and past briefings for the admin view
      const allVideos = [];
      if (videosRes.data.featured_video) {
        allVideos.push(videosRes.data.featured_video);
      }
      if (videosRes.data.past_briefings) {
        allVideos.push(...videosRes.data.past_briefings);
      }
      setCoachVideos(allVideos);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setDataLoading(false);
    }
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    if (!newVideo.title || !newVideo.video_url) {
      toast.error('Please fill in title and video URL');
      return;
    }
    
    setAddingVideo(true);
    try {
      const response = await api.post('/academy/admin/coach-corner/add-video', newVideo);
      if (response.data.success) {
        toast.success('Video added to Coach\'s Corner');
        setAddVideoOpen(false);
        setNewVideo({ title: '', description: '', video_url: '', duration: '', is_featured: false });
        loadAllData();
      }
    } catch (error) {
      toast.error('Failed to add video');
    } finally {
      setAddingVideo(false);
    }
  };

  const handleSendHypeEmail = async (email, name, emailType) => {
    setSendingHype(true);
    setHypeEmailType(emailType);
    try {
      const response = await api.post('/academy/admin/send-hype-email', {
        email,
        name,
        email_type: emailType
      });
      if (response.data.success) {
        toast.success(`"${emailType.replace('_', ' ')}" email sent to ${email}`);
      } else {
        toast.error(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send hype email');
    } finally {
      setSendingHype(false);
      setHypeEmailType(null);
    }
  };

  const handleSendBulkHypeEmail = async (emailType) => {
    if (!confirm(`Send "${emailType.replace('_', ' ')}" email to ALL non-enrolled leads?`)) return;
    
    setSendingHype(true);
    try {
      const response = await api.post(`/academy/admin/send-bulk-hype-email?email_type=${emailType}`);
      if (response.data.success) {
        toast.success(`Sent to ${response.data.sent_count} leads!`);
      }
    } catch (error) {
      toast.error('Failed to send bulk emails');
    } finally {
      setSendingHype(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('academy_admin_auth');
    setIsAuthenticated(false);
  };

  const toggleConsultationComplete = async (consultationId, currentStatus) => {
    try {
      await api.put(`/academy/admin/consultations/${consultationId}/complete`, {
        completed: !currentStatus
      });
      
      setConsultations(prev => prev.map(c => 
        c.id === consultationId 
          ? { ...c, completed: !currentStatus }
          : c
      ));
      
      toast.success(currentStatus ? 'Marked as pending' : 'Marked as complete');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const toggleIXLStatus = async (studentId, currentStatus) => {
    try {
      await api.put(`/academy/admin/students/${studentId}/ixl-status`, {
        ixl_screenshot_uploaded: !currentStatus
      });
      
      setRoster(prev => prev.map(s => 
        s.id === studentId 
          ? { ...s, ixl_screenshot_uploaded: !currentStatus }
          : s
      ));
      
      toast.success('IXL status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openScoutingReportDialog = (student) => {
    setSelectedStudent(student);
    setScoutingReport({
      week: 1,
      // SmartScore Metrics
      skillMastery: '',
      currentSmartScore: '',
      growthDelta: '',
      smartScoreTake: '',
      // Executive Functioning
      focusArea: '',
      focusStatus: 'ACCELERATING',
      executiveTake: '',
      // The Next Play
      primaryObjective: '',
      secondaryObjective: '',
      coachNote: ''
    });
    setScoutingReportOpen(true);
  };

  const sendEnhancedScoutingReport = async () => {
    if (!selectedStudent) return;
    
    setSendingReport(true);
    try {
      const response = await api.post('/academy/admin/send-enhanced-scouting-report', {
        student_id: selectedStudent.id,
        week: scoutingReport.week,
        // SmartScore Metrics
        skill_mastery: scoutingReport.skillMastery,
        current_smart_score: scoutingReport.currentSmartScore,
        growth_delta: scoutingReport.growthDelta,
        smart_score_take: scoutingReport.smartScoreTake,
        // Executive Functioning
        focus_area: scoutingReport.focusArea,
        focus_status: scoutingReport.focusStatus,
        executive_take: scoutingReport.executiveTake,
        // The Next Play
        primary_objective: scoutingReport.primaryObjective,
        secondary_objective: scoutingReport.secondaryObjective,
        coach_note: scoutingReport.coachNote
      });
      
      if (response.data.success) {
        toast.success(`Weekly scouting report sent to ${selectedStudent.name}`);
        setScoutingReportOpen(false);
      } else {
        toast.error('Failed to send report');
      }
    } catch (error) {
      toast.error('Failed to send report');
    } finally {
      setSendingReport(false);
    }
  };

  const sendScoutingReport = async (studentId, studentName) => {
    try {
      const response = await api.post('/academy/admin/send-scouting-report', {
        student_id: studentId
      });
      
      if (response.data.success) {
        toast.success(`Scouting report sent to ${studentName}`);
      } else {
        toast.error('Failed to send report');
      }
    } catch (error) {
      toast.error('Failed to send report');
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
        <Card className="bg-gray-900 border-gray-800 w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-orange-600/20 border-2 border-orange-600 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-orange-600" />
            </div>
            <CardTitle className="text-2xl font-black text-white">
              Command <span className="text-orange-600">Center</span>
            </CardTitle>
            <p className="text-gray-500 text-sm">Admin access required</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="bg-white/10 border-gray-700 text-white"
              />
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-4"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Access Command Center'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      <div className="container mx-auto px-6 py-8">
        {/* Header with Logo */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <img 
              src="/images/scholar-athlete-logo.png" 
              alt="Scholar Athlete Academy" 
              className="h-12 w-auto"
              style={{ filter: 'invert(37%) sepia(100%) saturate(2500%) hue-rotate(2deg) brightness(105%) contrast(105%)' }}
            />
            <div>
              <h1 className="text-3xl font-black">
                Command <span className="text-orange-600">Center</span>
              </h1>
              <p className="text-gray-500">Scholar Athlete Academy • The PRSVR School</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              onClick={loadAllData}
              variant="outline"
              className="border-gray-700 text-gray-400"
              disabled={dataLoading}
            >
              {dataLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
            </Button>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="border-gray-700 text-gray-400"
            >
              Log Out
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="py-4 text-center">
              <div className="text-3xl font-black text-orange-600">{leads.length}</div>
              <div className="text-gray-500 text-sm">Total Leads</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="py-4 text-center">
              <div className="text-3xl font-black text-green-500">{roster.length}</div>
              <div className="text-gray-500 text-sm">Enrolled Students</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="py-4 text-center">
              <div className="text-3xl font-black text-blue-500">
                {roster.filter(s => s.subscription_tier === 'elite_pivot').length}
              </div>
              <div className="text-gray-500 text-sm">Elite Members</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="py-4 text-center">
              <div className="text-3xl font-black text-purple-500">
                {consultations.filter(c => !c.completed).length}
              </div>
              <div className="text-gray-500 text-sm">Pending Calls</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-800 pb-4 overflow-x-auto">
          {[
            { id: 'leads', label: 'Lead Tracker', icon: Target, count: leads.length },
            { id: 'submissions', label: 'Quiz Submissions', icon: FileText, count: submissions.length },
            { id: 'roster', label: 'Student Roster', icon: Users, count: roster.length },
            { id: 'consultations', label: 'Consultations', icon: Calendar, count: consultations.length },
            { id: 'coachcorner', label: "Coach's Corner", icon: Video, count: coachVideos.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === tab.id 
                  ? 'bg-orange-600 text-white' 
                  : 'bg-gray-900 text-gray-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              <span className="text-xs bg-black/30 px-2 py-0.5 rounded">{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Lead Tracker Tab */}
        {activeTab === 'leads' && (
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-orange-600" />
                Lead Tracker
              </CardTitle>
              {/* Hype Sequence Bulk Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={() => handleSendBulkHypeEmail('call_out')}
                  disabled={sendingHype}
                  size="sm"
                  variant="outline"
                  className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10 text-xs"
                >
                  <Send className="w-3 h-3 mr-1" />
                  Call Out All
                </Button>
                <Button
                  onClick={() => handleSendBulkHypeEmail('assessment')}
                  disabled={sendingHype}
                  size="sm"
                  variant="outline"
                  className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10 text-xs"
                >
                  <Send className="w-3 h-3 mr-1" />
                  Assessment All
                </Button>
                <Button
                  onClick={() => handleSendBulkHypeEmail('close')}
                  disabled={sendingHype}
                  size="sm"
                  variant="outline"
                  className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10 text-xs"
                >
                  <Send className="w-3 h-3 mr-1" />
                  Close All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Hype Sequence Legend */}
              <div className="mb-4 p-3 bg-gray-800/50 rounded-lg text-xs">
                <span className="text-gray-400 font-bold mr-3">HYPE SEQUENCE:</span>
                <span className="text-blue-500 mr-3">1. Call Out</span>
                <span className="text-yellow-500 mr-3">2. Assessment</span>
                <span className="text-orange-500">3. Close</span>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left py-3 px-4 text-gray-500 text-sm font-medium">Name</th>
                      <th className="text-left py-3 px-4 text-gray-500 text-sm font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-gray-500 text-sm font-medium">Sport</th>
                      <th className="text-left py-3 px-4 text-gray-500 text-sm font-medium">Zone</th>
                      <th className="text-left py-3 px-4 text-gray-500 text-sm font-medium">Score</th>
                      <th className="text-left py-3 px-4 text-gray-500 text-sm font-medium">Hype Sequence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead, idx) => {
                      const zone = ZONE_CONFIG[lead.readiness_zone] || ZONE_CONFIG.elite;
                      return (
                        <tr key={idx} className="border-b border-gray-800/50 hover:bg-white/5">
                          <td className="py-3 px-4 text-white font-medium">{lead.name}</td>
                          <td className="py-3 px-4 text-gray-400 text-sm">{lead.email}</td>
                          <td className="py-3 px-4 text-gray-400">{lead.sport}</td>
                          <td className="py-3 px-4">
                            <span 
                              className="px-2 py-1 text-xs font-bold uppercase"
                              style={{ backgroundColor: `${zone.color}20`, color: zone.color }}
                            >
                              {zone.label}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-white font-bold">{lead.total_score}/50</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleSendHypeEmail(lead.email, lead.name, 'call_out')}
                                disabled={sendingHype}
                                className="w-7 h-7 rounded bg-blue-500/20 text-blue-500 hover:bg-blue-500/30 flex items-center justify-center text-xs font-bold disabled:opacity-50"
                                title="Send Call Out"
                              >
                                {sendingHype && hypeEmailType === 'call_out' ? <Loader2 className="w-3 h-3 animate-spin" /> : '1'}
                              </button>
                              <button
                                onClick={() => handleSendHypeEmail(lead.email, lead.name, 'assessment')}
                                disabled={sendingHype}
                                className="w-7 h-7 rounded bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 flex items-center justify-center text-xs font-bold disabled:opacity-50"
                                title="Send Assessment"
                              >
                                {sendingHype && hypeEmailType === 'assessment' ? <Loader2 className="w-3 h-3 animate-spin" /> : '2'}
                              </button>
                              <button
                                onClick={() => handleSendHypeEmail(lead.email, lead.name, 'close')}
                                disabled={sendingHype}
                                className="w-7 h-7 rounded bg-orange-500/20 text-orange-500 hover:bg-orange-500/30 flex items-center justify-center text-xs font-bold disabled:opacity-50"
                                title="Send Close"
                              >
                                {sendingHype && hypeEmailType === 'close' ? <Loader2 className="w-3 h-3 animate-spin" /> : '3'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {leads.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-gray-500">
                          No leads yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quiz Submissions Tab */}
        {activeTab === 'submissions' && (
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-500" />
                Quiz & Diagnostic Submissions
              </CardTitle>
              <div className="flex gap-4 text-sm">
                <span className="text-orange-400">
                  Students: {submissionsBreakdown.student_diagnostics || 0}
                </span>
                <span className="text-purple-400">
                  Parents: {submissionsBreakdown.parent_diagnostics || 0}
                </span>
                <span className="text-green-400">
                  Reclass Leads: {submissionsBreakdown.reclassification_leads || 0}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Type</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Name</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Details</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Score</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-gray-500">
                          No submissions yet
                        </td>
                      </tr>
                    ) : (
                      submissions.map((sub, idx) => (
                        <tr key={idx} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              sub.type === 'Student Diagnostic' ? 'bg-orange-500/20 text-orange-400' :
                              sub.type === 'Parent Diagnostic' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-green-500/20 text-green-400'
                            }`}>
                              {sub.type}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-white font-medium">{sub.name}</td>
                          <td className="py-3 px-4 text-gray-400">{sub.email}</td>
                          <td className="py-3 px-4 text-gray-400 text-sm">
                            {sub.type === 'Student Diagnostic' && (
                              <span>Grade: {sub.grade} | Sport: {sub.sport}</span>
                            )}
                            {sub.type === 'Parent Diagnostic' && (
                              <span>Student: {sub.student_name}</span>
                            )}
                            {sub.type === 'Reclassification Lead' && (
                              <span>Grade: {sub.student_grade} | Goal: {sub.recruitment_goal}</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {sub.total_score ? (
                              <span className={`font-bold ${
                                sub.total_score >= 80 ? 'text-green-400' :
                                sub.total_score >= 60 ? 'text-yellow-400' :
                                'text-red-400'
                              }`}>
                                {sub.total_score}%
                              </span>
                            ) : (
                              <span className="text-gray-600">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-gray-500 text-sm">
                            {sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Student Roster Tab */}
        {activeTab === 'roster' && (
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-600" />
                Student Roster
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left py-3 px-4 text-gray-500 text-sm font-medium">Student</th>
                      <th className="text-left py-3 px-4 text-gray-500 text-sm font-medium">Tier</th>
                      <th className="text-left py-3 px-4 text-gray-500 text-sm font-medium">Payment</th>
                      <th className="text-left py-3 px-4 text-gray-500 text-sm font-medium">IXL Diagnostic</th>
                      <th className="text-left py-3 px-4 text-gray-500 text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roster.map((student, idx) => {
                      const tier = TIER_CONFIG[student.subscription_tier] || TIER_CONFIG.baseline;
                      const TierIcon = tier.icon;
                      const zone = ZONE_CONFIG[student.readiness_zone];
                      
                      return (
                        <tr key={idx} className="border-b border-gray-800/50 hover:bg-white/5">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                                style={{ backgroundColor: `${tier.color}20`, color: tier.color }}
                              >
                                {student.name?.charAt(0)}
                              </div>
                              <div>
                                <div className="text-white font-medium">{student.name}</div>
                                <div className="text-gray-500 text-sm">{student.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span 
                              className="px-2 py-1 text-xs font-bold uppercase flex items-center gap-1 w-fit"
                              style={{ backgroundColor: `${tier.color}20`, color: tier.color }}
                            >
                              <TierIcon className="w-3 h-3" />
                              {tier.badge}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {student.payment_status === 'verified' ? (
                              <span className="text-green-500 flex items-center gap-1 text-sm">
                                <CheckCircle className="w-4 h-4" /> Verified
                              </span>
                            ) : (
                              <span className="text-yellow-500 flex items-center gap-1 text-sm">
                                <AlertCircle className="w-4 h-4" /> Pending
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {student.ixl_screenshot_url ? (
                                <a 
                                  href={student.ixl_screenshot_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  <img 
                                    src={student.ixl_screenshot_url} 
                                    alt="IXL Screenshot"
                                    className="w-16 h-12 object-cover rounded border border-green-600/30 hover:border-green-500 transition-colors"
                                  />
                                </a>
                              ) : (
                                <button
                                  onClick={() => toggleIXLStatus(student.id, student.ixl_screenshot_uploaded)}
                                  className={`px-3 py-1 rounded text-xs font-bold flex items-center gap-1 ${
                                    student.ixl_screenshot_uploaded
                                      ? 'bg-green-600/20 text-green-500'
                                      : 'bg-gray-800 text-gray-500 hover:text-white'
                                  }`}
                                >
                                  {student.ixl_screenshot_uploaded ? (
                                    <><Check className="w-3 h-3" /> Uploaded</>
                                  ) : (
                                    <><X className="w-3 h-3" /> Not Yet</>
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              onClick={() => openScoutingReportDialog(student)}
                              size="sm"
                              className="bg-orange-600/20 text-orange-600 hover:bg-orange-600 hover:text-white text-xs"
                            >
                              <Mail className="w-3 h-3 mr-1" />
                              Send Report
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {roster.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-gray-500">
                          No enrolled students yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Consultations Tab */}
        {activeTab === 'consultations' && (
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-600" />
                Consultation Manager
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {consultations.map((consultation, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg border ${
                      consultation.completed 
                        ? 'bg-green-600/5 border-green-600/30' 
                        : 'bg-gray-800/50 border-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => toggleConsultationComplete(consultation.id, consultation.completed)}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                            consultation.completed
                              ? 'bg-green-600 border-green-600'
                              : 'border-gray-600 hover:border-orange-600'
                          }`}
                        >
                          {consultation.completed && <Check className="w-4 h-4 text-white" />}
                        </button>
                        
                        <div>
                          <div className="text-white font-bold">{consultation.athlete_name}</div>
                          <div className="text-gray-500 text-sm">
                            Parent: {consultation.parent_name}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-orange-600 font-bold">
                          {new Date(consultation.selected_date).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </div>
                        <div className="text-white">
                          {consultation.selected_time?.replace(/^(\d{2}):(\d{2})$/, (_, h, m) => {
                            const hour = parseInt(h);
                            const ampm = hour >= 12 ? 'PM' : 'AM';
                            const hour12 = hour % 12 || 12;
                            return `${hour12}:${m} ${ampm}`;
                          })}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-gray-700/50 flex items-center gap-4 text-sm">
                      <a 
                        href={`mailto:${consultation.parent_email}`}
                        className="text-gray-400 hover:text-orange-600 flex items-center gap-1"
                      >
                        <Mail className="w-4 h-4" />
                        {consultation.parent_email}
                      </a>
                      <a 
                        href={`tel:${consultation.parent_phone}`}
                        className="text-gray-400 hover:text-orange-600 flex items-center gap-1"
                      >
                        <Phone className="w-4 h-4" />
                        {consultation.parent_phone}
                      </a>
                      {consultation.readiness_zone && (
                        <span 
                          className="px-2 py-0.5 text-xs font-bold uppercase"
                          style={{ 
                            backgroundColor: `${ZONE_CONFIG[consultation.readiness_zone]?.color}20`,
                            color: ZONE_CONFIG[consultation.readiness_zone]?.color
                          }}
                        >
                          {ZONE_CONFIG[consultation.readiness_zone]?.label}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {consultations.length === 0 && (
                  <div className="py-8 text-center text-gray-500">
                    No consultations booked yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Coach's Corner Tab */}
        {activeTab === 'coachcorner' && (
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Video className="w-5 h-5 text-orange-600" />
                Coach's Corner Videos
              </CardTitle>
              <Button 
                onClick={() => setAddVideoOpen(true)}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Video
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400 text-sm mb-6">
                Upload quick scouting reports or motivational videos. Students will see these in their High-Performance Dashboard.
              </p>
              
              <div className="space-y-4">
                {coachVideos.map((video, idx) => (
                  <div 
                    key={video.id || idx}
                    className={`p-4 rounded-lg border ${
                      video.is_featured 
                        ? 'bg-orange-500/10 border-orange-500/50' 
                        : 'bg-gray-800/50 border-gray-700'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Video Thumbnail Placeholder */}
                      <div className="w-24 h-32 bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                        <Video className="w-8 h-8 text-gray-500" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {video.is_featured && (
                            <span className="px-2 py-0.5 bg-orange-600 text-white text-xs font-bold uppercase rounded">
                              Featured
                            </span>
                          )}
                          <h4 className="text-white font-bold">{video.title}</h4>
                        </div>
                        <p className="text-gray-400 text-sm mb-2">{video.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          {video.duration && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {video.duration}
                            </span>
                          )}
                          {video.created_at && (
                            <span>
                              Added {new Date(video.created_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <div className="mt-2">
                          <a 
                            href={video.video_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-orange-500 text-sm hover:underline"
                          >
                            View Video →
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {coachVideos.length === 0 && (
                  <div className="py-12 text-center">
                    <Video className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">No videos yet</p>
                    <p className="text-gray-600 text-sm">Click "Add Video" to upload your first Coach's Corner briefing</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Video Dialog */}
      <Dialog open={addVideoOpen} onOpenChange={setAddVideoOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Video className="w-5 h-5 text-orange-600" />
              Add Coach's Corner Video
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddVideo} className="space-y-4 mt-4">
            <div>
              <Label className="text-white mb-2 block">Video Title *</Label>
              <Input
                value={newVideo.title}
                onChange={(e) => setNewVideo({...newVideo, title: e.target.value})}
                placeholder="e.g., Weekly Scouting Report #3"
                className="bg-gray-800 border-gray-700 text-white"
                required
              />
            </div>
            
            <div>
              <Label className="text-white mb-2 block">Video URL *</Label>
              <Input
                value={newVideo.video_url}
                onChange={(e) => setNewVideo({...newVideo, video_url: e.target.value})}
                placeholder="YouTube or Loom embed URL (e.g., https://youtube.com/embed/...)"
                className="bg-gray-800 border-gray-700 text-white"
                required
              />
              <p className="text-gray-500 text-xs mt-1">
                Paste the embed URL from YouTube, Loom, or similar platforms
              </p>
            </div>
            
            <div>
              <Label className="text-white mb-2 block">Description</Label>
              <Textarea
                value={newVideo.description}
                onChange={(e) => setNewVideo({...newVideo, description: e.target.value})}
                placeholder="Brief description of the video content..."
                className="bg-gray-800 border-gray-700 text-white"
                rows={3}
              />
            </div>
            
            <div>
              <Label className="text-white mb-2 block">Duration (optional)</Label>
              <Input
                value={newVideo.duration}
                onChange={(e) => setNewVideo({...newVideo, duration: e.target.value})}
                placeholder="e.g., 5:30"
                className="bg-gray-800 border-gray-700 text-white w-32"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_featured"
                checked={newVideo.is_featured}
                onChange={(e) => setNewVideo({...newVideo, is_featured: e.target.checked})}
                className="w-4 h-4 accent-orange-600"
              />
              <Label htmlFor="is_featured" className="text-white cursor-pointer">
                Set as Featured Video (displays prominently at top)
              </Label>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddVideoOpen(false)}
                className="flex-1 border-gray-700 text-gray-300"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={addingVideo}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                {addingVideo ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Video
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Enhanced Scouting Report Dialog */}
      <Dialog open={scoutingReportOpen} onOpenChange={setScoutingReportOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <img 
                src="/images/scholar-athlete-logo.png" 
                alt="Scholar Athlete Academy" 
                className="h-10 w-auto"
                style={{ filter: 'invert(37%) sepia(100%) saturate(2500%) hue-rotate(2deg) brightness(105%) contrast(105%)' }}
              />
              <div>
                <div className="text-orange-600 font-black uppercase tracking-wider">Scholar Athlete Academy</div>
                <div className="text-gray-400 text-xs font-bold uppercase tracking-widest">Pace & Pivot • Weekly Scouting Report</div>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedStudent && (
            <div className="space-y-6">
              {/* Player Header */}
              <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
                    style={{ 
                      backgroundColor: `${TIER_CONFIG[selectedStudent.subscription_tier]?.color}20`, 
                      color: TIER_CONFIG[selectedStudent.subscription_tier]?.color 
                    }}
                  >
                    {selectedStudent.name?.charAt(0)}
                  </div>
                  <div>
                    <div className="text-white font-bold text-lg">{selectedStudent.name}</div>
                    <div className="text-gray-500 text-sm">{selectedStudent.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <label className="text-gray-500 text-xs block mb-1">WEEK</label>
                    <select 
                      value={scoutingReport.week}
                      onChange={(e) => setScoutingReport(prev => ({ ...prev, week: parseInt(e.target.value) }))}
                      className="bg-gray-800 border border-gray-700 text-white px-3 py-1 rounded font-bold"
                    >
                      {[1, 2, 3, 4].map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                  <div className="text-right">
                    <label className="text-gray-500 text-xs block mb-1">TIER</label>
                    <span 
                      className="px-3 py-1 text-sm font-bold uppercase inline-block"
                      style={{ 
                        backgroundColor: `${TIER_CONFIG[selectedStudent.subscription_tier]?.color}20`, 
                        color: TIER_CONFIG[selectedStudent.subscription_tier]?.color 
                      }}
                    >
                      {selectedStudent.subscription_tier === 'elite_pivot' ? 'Elite' : 'Major'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Section I: SmartScore Metrics */}
              <div className="border border-orange-600/30 rounded-lg overflow-hidden">
                <div className="bg-orange-600/20 px-4 py-2 border-b border-orange-600/30">
                  <h3 className="text-orange-600 font-black text-sm tracking-wider">I. THE SMART-SCORE METRICS (IXL DATA)</h3>
                </div>
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-gray-400 text-xs block mb-1">Skill Mastery</label>
                      <Input
                        value={scoutingReport.skillMastery}
                        onChange={(e) => setScoutingReport(prev => ({ ...prev, skillMastery: e.target.value }))}
                        placeholder="e.g., Algebraic Foundations"
                        className="bg-gray-800 border-gray-700 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-xs block mb-1">Current SmartScore</label>
                      <Input
                        value={scoutingReport.currentSmartScore}
                        onChange={(e) => setScoutingReport(prev => ({ ...prev, currentSmartScore: e.target.value }))}
                        placeholder="e.g., 88/100"
                        className="bg-gray-800 border-gray-700 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-xs block mb-1">Growth Delta</label>
                      <Input
                        value={scoutingReport.growthDelta}
                        onChange={(e) => setScoutingReport(prev => ({ ...prev, growthDelta: e.target.value }))}
                        placeholder="e.g., +12 Points"
                        className="bg-gray-800 border-gray-700 text-white text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs block mb-1">The Scout's Take</label>
                    <Textarea
                      value={scoutingReport.smartScoreTake}
                      onChange={(e) => setScoutingReport(prev => ({ ...prev, smartScoreTake: e.target.value }))}
                      placeholder="e.g., We saw a massive shift in confidence with multi-step equations. He cleared the 'mental clog' he had on Monday..."
                      className="bg-gray-800 border-gray-700 text-white text-sm min-h-[80px]"
                    />
                  </div>
                </div>
              </div>
              
              {/* Section II: Executive Functioning */}
              <div className="border border-orange-600/30 rounded-lg overflow-hidden">
                <div className="bg-orange-600/20 px-4 py-2 border-b border-orange-600/30">
                  <h3 className="text-orange-600 font-black text-sm tracking-wider">II. EXECUTIVE FUNCTIONING (SYSTEMS)</h3>
                </div>
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-400 text-xs block mb-1">Focus Area</label>
                      <Input
                        value={scoutingReport.focusArea}
                        onChange={(e) => setScoutingReport(prev => ({ ...prev, focusArea: e.target.value }))}
                        placeholder="e.g., Time Management/Consistency"
                        className="bg-gray-800 border-gray-700 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-xs block mb-1">Status</label>
                      <select 
                        value={scoutingReport.focusStatus}
                        onChange={(e) => setScoutingReport(prev => ({ ...prev, focusStatus: e.target.value }))}
                        className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded text-sm"
                      >
                        <option value="ACCELERATING">🚀 ACCELERATING</option>
                        <option value="ON_TRACK">✅ ON TRACK</option>
                        <option value="NEEDS_ATTENTION">⚠️ NEEDS ATTENTION</option>
                        <option value="STALLED">🔴 STALLED</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs block mb-1">The Scout's Take</label>
                    <Textarea
                      value={scoutingReport.executiveTake}
                      onChange={(e) => setScoutingReport(prev => ({ ...prev, executiveTake: e.target.value }))}
                      placeholder="e.g., Calendar mastery is improving. He logged in before 10 AM every day this week, showing the 'Pro Habits' we expect..."
                      className="bg-gray-800 border-gray-700 text-white text-sm min-h-[80px]"
                    />
                  </div>
                </div>
              </div>
              
              {/* Section III: The Next Play */}
              <div className="border border-orange-600/30 rounded-lg overflow-hidden">
                <div className="bg-orange-600/20 px-4 py-2 border-b border-orange-600/30">
                  <h3 className="text-orange-600 font-black text-sm tracking-wider">III. THE NEXT PLAY (WEEK {scoutingReport.week + 1 > 4 ? 4 : scoutingReport.week + 1} TARGETS)</h3>
                </div>
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-400 text-xs block mb-1">Primary Objective</label>
                      <Input
                        value={scoutingReport.primaryObjective}
                        onChange={(e) => setScoutingReport(prev => ({ ...prev, primaryObjective: e.target.value }))}
                        placeholder="e.g., Ratios & Proportions"
                        className="bg-gray-800 border-gray-700 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-xs block mb-1">Secondary Objective</label>
                      <Input
                        value={scoutingReport.secondaryObjective}
                        onChange={(e) => setScoutingReport(prev => ({ ...prev, secondaryObjective: e.target.value }))}
                        placeholder="e.g., Reading Comprehension: Inference"
                        className="bg-gray-800 border-gray-700 text-white text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs block mb-1">Coach's Note</label>
                    <Textarea
                      value={scoutingReport.coachNote}
                      onChange={(e) => setScoutingReport(prev => ({ ...prev, coachNote: e.target.value }))}
                      placeholder="e.g., Next week is about 'Reps.' I want to see a SmartScore of 90+ in at least two new categories by Wednesday."
                      className="bg-gray-800 border-gray-700 text-white text-sm min-h-[60px]"
                    />
                  </div>
                </div>
              </div>
              
              {/* Footer Preview */}
              <div className="text-center py-3 border-t border-gray-800">
                <p className="text-orange-600 text-xs font-bold tracking-widest">THE FIRST MOVE IS THE PIVOT. PERSEVERE.</p>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={() => setScoutingReportOpen(false)}
                  variant="outline"
                  className="flex-1 border-gray-700 text-gray-400"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={sendEnhancedScoutingReport}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-bold"
                  disabled={sendingReport || !scoutingReport.smartScoreTake.trim()}
                >
                  {sendingReport ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Mail className="w-4 h-4 mr-2" /> Send Report</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AcademyAdminPortal;
